'use client'

import classNames from 'clsx'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'

import CheckIcon from '@/assets/icons/check-sm.svg'
import ChevronDown from '@/assets/icons/leaderboard/lb-chevron-down-16.svg'
import useClickOutside from '@/hooks/useClickOutside'

import S from './FilterDropdown.module.scss'

export type FilterOption = { value: string; label: string }

type FilterDropdownProps = {
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<FilterOption>
  /** Accessible name of the trigger, e.g. "Sport". */
  label: string
}

/** Leaderboard filter (Figma "LB Filter / Dropdown"). */
export default function FilterDropdown({
  value,
  onChange,
  options,
  label
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useClickOutside<HTMLDivElement>(open, close)
  const listId = useId()

  const selected = useMemo(
    () => options.find(option => option.value === value) ?? options[0],
    [options, value]
  )

  useEffect(() => {
    if (!open) {
      return
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  if (!selected) {
    return null
  }

  return (
    <div className={S.dropdown} ref={ref}>
      <button
        type="button"
        className={classNames(S.trigger, open && S.triggerOpen)}
        aria-label={`${label}: ${selected.label}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen(isOpen => !isOpen)}
      >
        <span className={S.triggerText}>{selected.label}</span>
        <ChevronDown
          className={classNames(S.chevron, open && S.chevronOpen)}
          aria-hidden
        />
      </button>

      {open ? (
        <ul id={listId} className={S.menu} role="listbox" aria-label={label}>
          {options.map(option => {
            const isSelected = option.value === selected.value
            return (
              <li key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(
                    S.option,
                    isSelected && S.optionSelected
                  )}
                  onClick={() => {
                    onChange(option.value)
                    close()
                  }}
                >
                  <span className={S.optionLabel}>{option.label}</span>
                  {isSelected ? (
                    <CheckIcon className={S.check} aria-hidden />
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
